import { IOptions } from '../common/common.types'
import {
  IBodyParts,
  IBodySurveyFinding,
  IExamData,
  IExamValue,
} from '../features/additionalAssessments/bodyAssessment/bodySurvey/store/bodySurvey.types'
import { BodyAssessmentConstants } from '../features/additionalAssessments/bodyAssessment/store/bodyAssessment.constants'

import abdomenData from '../features/additionalAssessments/bodyAssessment/bodySurvey/data/abdomen.json'
import chestLungData from '../features/additionalAssessments/bodyAssessment/bodySurvey/data/chestLung.json'
import posteriorChestLungData from '../features/additionalAssessments/bodyAssessment/bodySurvey/data/posteriorChestLung.json'
import rightFootData from '../features/additionalAssessments/bodyAssessment/bodySurvey/data/rightFoot.json'
import leftFootData from '../features/additionalAssessments/bodyAssessment/bodySurvey/data/leftFoot.json'
import rightHandData from '../features/additionalAssessments/bodyAssessment/bodySurvey/data/rightHand.json'
import leftHandData from '../features/additionalAssessments/bodyAssessment/bodySurvey/data/leftHand.json'
import faceData from '../features/additionalAssessments/bodyAssessment/bodySurvey/data/face.json'
import adultBodyFront from '../features/additionalAssessments/bodyAssessment/bodySurvey/data/adultBodyFront.json'
import adultBodyBack from '../features/additionalAssessments/bodyAssessment/bodySurvey/data/adultBodyBack.json'

export const examMapping: any = {
  [BodyAssessmentConstants.GENERAL_EXTREMITIES]: [{ id: 10, name: 'General Extremities' }],
  [BodyAssessmentConstants.EXTERNAL_SKIN]: [{ id: 1, name: 'External/Skin' }],
  [BodyAssessmentConstants.CARDIO_CONDITIONS]: [{ id: 6, name: 'Heart' }],
  [BodyAssessmentConstants.MENTAL_CONDITIONS]: [{ id: 12, name: 'Mental' }],
  [BodyAssessmentConstants.NEURO_CONDITIONS]: [{ id: 13, name: 'Neuro' }],
}

const mergedJsonData = [
  ...abdomenData,
  ...chestLungData,
  ...posteriorChestLungData,
  ...rightFootData,
  ...leftFootData,
  ...rightHandData,
  ...leftHandData,
  ...adultBodyBack,
  ...adultBodyFront,
  ...faceData,
  ...examMapping[BodyAssessmentConstants.GENERAL_EXTREMITIES],
  ...examMapping[BodyAssessmentConstants.EXTERNAL_SKIN],
  ...examMapping[BodyAssessmentConstants.CARDIO_CONDITIONS],
  ...examMapping[BodyAssessmentConstants.MENTAL_CONDITIONS],
  ...examMapping[BodyAssessmentConstants.NEURO_CONDITIONS],
]

export const LOCATION_ID_MAPPING: any = {
  12: 'Mental',
  13: 'Neuro',
  6: 'Heart',
  10: 'General Extremities',
  1: 'External/Skin',
  default: 'Body Parts',
}

// Built once on first lookup (not at import) from the same merged body-part data,
// giving O(1) id -> name resolution without scanning the array per call.
let locationNameById: Map<number, string> | null = null

export const getBodyPartNameById = (locationId: number | string | null | undefined): string | undefined => {
  if (locationId === null || locationId === undefined || locationId === '') return undefined
  if (!locationNameById) {
    locationNameById = new Map<number, string>()
    // First occurrence wins, matching the `.find` order used by setLocationNames.
    mergedJsonData.forEach((location) => {
      if (!locationNameById!.has(location.id)) locationNameById!.set(location.id, location.name)
    })
  }
  return locationNameById.get(Number(locationId))
}

export function segregateOptions(data: IExamValue[], locationData: IOptions): IExamValue[] {
  if (!data || !Array.isArray(data)) {
    return []
  }

  const locationMap = new Map<any, any>()
  locationData.forEach((location) => {
    locationMap.set(location.id, location.name)
  })

  const result: any[] = []
  data.forEach((item: any) => {
    if (item.originalLocationIds && Array.isArray(item.originalLocationIds)) {
      item.originalLocationIds.forEach((originalLocation: any) => {
        const { locationId, optionId, examId, ...otherProps } = originalLocation
        result.push({
          id: optionId,
          name: item.name,
          locationId,
          examId,
          segmentId: item.segmentId,
          __locationName: locationMap.get(locationId) || '',
          ...otherProps,
        })
      })
    }
  })

  return result
}

export const EXAM_NAME_MAPPING: any = {
  Neuro: BodyAssessmentConstants.NEURO_CONDITIONS,
  Mental: BodyAssessmentConstants.MENTAL_CONDITIONS,
  Heart: BodyAssessmentConstants.CARDIO_CONDITIONS,
  'General Extremities': BodyAssessmentConstants.GENERAL_EXTREMITIES,
  'External/Skin': BodyAssessmentConstants.EXTERNAL_SKIN,
  'Body Parts': BodyAssessmentConstants.BODY_PARTS,
}

export const getFilteredOptions = (selectedExam: string, options: any[]) => {
  const examName = Object.keys(EXAM_NAME_MAPPING).find((key) => EXAM_NAME_MAPPING[key] === selectedExam)
  if (!examName) return []

  const filteredOptions = options.filter((option) => {
    const optionExamName = LOCATION_ID_MAPPING[option.locationId] || LOCATION_ID_MAPPING.default
    return optionExamName === examName
  })

  const combinedOptions = filteredOptions.reduce((acc, curr) => {
    const existingOption = acc.find((opt: any) => opt.__examName === examName)
    if (existingOption) {
      existingOption.options.push(...curr.options)
    } else {
      acc.push({
        __examName: examName,
        options: [...curr.options],
      })
    }
    return acc
  }, [])

  const flattenedOptions = combinedOptions.flatMap((exam: any) =>
    exam.options.map((opt: any) => ({
      ...opt,
      __examName: exam.__examName,
    }))
  )

  return flattenedOptions
}

export const transformNeuroMentalData = (examName: string, data: IExamValue[]): any[] => {
  return data
    .filter((item) => item.examId !== undefined || item.id !== undefined)
    .map((item) => ({
      customCommandFacId: item.id,
      id: item.examId,
      name: item.name,
      locationId: item.locationId,
      segmentId: item.segmentId,
      __examName: examName,
    }))
}

export const reverseTransformationOnNeuroMentalData = (data: IExamValue[]): any[] => {
  return data?.map((item) => ({
    ...item,
    id: item.customCommandFacId,
    examId: item.id,
  }))
}

export function groupCommonOptions(data: any[]) {
  const combinedOptions = data.reduce((acc: any[], item: any) => {
    const option = {
      ...item,
      id: item.id,
      name: item.name,
      locationId: item.locationId,
      segmentId: item.segmentId,
    }
    acc.push(option)
    return acc
  }, [])

  const map = new Map<string, any>()

  combinedOptions.forEach((option: any) => {
    const { id, name, locationId, examId } = option

    if (map.has(name)) {
      map.get(name).originalLocationIds.push({ locationId, optionId: id, examId })
    } else {
      map.set(name, { ...option, originalLocationIds: [{ locationId, optionId: id, examId }] })
    }
  })
  const filteredValues = Array.from(map.values()).filter((item) => item.originalLocationIds.length > 1)
  if (filteredValues.length === 0) {
    return combinedOptions
  }

  return filteredValues
}

export const getDocumentedLocations = (arr1: IExamValue[], arr2: IBodySurveyFinding[]): IOptions => {
  const mergedArray = [...arr1, ...arr2]
  const extremitiesAndSkinLocationIds = [1, 10]
  const uniqueIds = new Set<number>()
  const uniqueLocations: IOptions = []

  mergedArray.forEach((item) => {
    if (!uniqueIds.has(item.locationId)) {
      uniqueIds.add(item.locationId)
      const locationName =
        (item as IExamValue).__locationName ||
        (item as IExamValue).__examName ||
        (item as IBodySurveyFinding).__locationName
      if (locationName) {
        uniqueLocations.push({ id: item.locationId, name: locationName })
      }
    }
  })

  const extremitiesAndSkinLocations = uniqueLocations.filter(
    (item) => item.id !== undefined && extremitiesAndSkinLocationIds.includes(item.id)
  )
  const otherLocations = uniqueLocations.filter(
    (item) => item.id === undefined || !extremitiesAndSkinLocationIds.includes(item.id)
  )

  const sortedLocations = [
    ...otherLocations,
    ...extremitiesAndSkinLocations.filter((item) => item.id === 1),
    ...extremitiesAndSkinLocations.filter((item) => item.id === 10),
  ]

  return sortedLocations
}

export const setLocationNames = (examOptions: IExamData[]) => {
  examOptions.forEach((exam) => {
    const location = mergedJsonData.find((loc) => loc.id === exam.locationId)
    if (location) {
      exam.__locationName = location.name
      exam.segmentId = 1
    }
  })
  return examOptions
}

export const checkSelectedBodyPartsHasSameComments = (selectedBodyParts: IBodyParts[], data: any) => {
  const comments: string[] = []

  for (const bodyPart of selectedBodyParts) {
    const matchingFindings = data.filter((d: { locationId: number }) => d.locationId === bodyPart.id)
    if (matchingFindings.length === 0) {
      return false
    }
    matchingFindings.forEach((d: { comments: any }) => comments.push(d.comments))
  }
  const firstComment = comments[0]
  const allCommentsSame = comments.every((comment) => comment === firstComment)

  return allCommentsSame
}

export const checkBodyPartsAndDataMatch = (selectedBodyParts: IBodyParts[], data: IExamValue[]) => {
  for (const bodyPart of selectedBodyParts) {
    const matchingEntries = data.filter((entry) => entry.locationId === bodyPart.id)

    if (matchingEntries.length === 0) {
      return false
    }

    const groupedEntries = matchingEntries.reduce((acc, entry) => {
      const key = `${entry.segmentId}-${entry.name}`
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(entry)
      return acc
    }, {} as Record<string, IExamValue[]>)

    for (const otherBodyPart of selectedBodyParts) {
      if (otherBodyPart.id === bodyPart.id) continue

      const otherMatchingEntries = data.filter((entry) => entry.locationId === otherBodyPart.id)

      if (otherMatchingEntries.length === 0) {
        return false
      }

      const otherGroupedEntries = otherMatchingEntries.reduce((acc, entry) => {
        const key = `${entry.segmentId}-${entry.name}`
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(entry)
        return acc
      }, {} as Record<string, IExamValue[]>)

      for (const key in groupedEntries) {
        if (!otherGroupedEntries[key] || otherGroupedEntries[key].length !== groupedEntries[key].length) {
          return false
        }
      }
    }
  }

  return true
}

export const filterCommonNamedOptions = (assessmentOptions: IExamValue[]) => {
  const groupedByName = assessmentOptions.reduce((acc: { [key: string]: IExamValue[] }, item: IExamValue) => {
    if (!acc[item?.name]) {
      acc[item.name] = []
    }
    acc[item.name].push(item)
    return acc
  }, {})
  const duplicates = Object.values(groupedByName).filter((group) => group?.length > 1)
  const duplicatesFlattened = duplicates.flat()
  const nonDuplicates = Object.values(groupedByName)
    .filter((group) => group?.length === 1)
    .flat()
  return { duplicates: duplicatesFlattened, nonDuplicates }
}

export const LEFT_RIGHT_LABELS = {
  front: { firstChip: 'R', secondChip: 'L' },
  back: { firstChip: 'L', secondChip: 'R' },
}
